/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { components } from 'react-select';
import { Options } from 'react-select';
import debounce from 'lodash/debounce';
import {
  AddressAutoSearch,
  AddressAutoSearchProps,
} from '@src/v3/interfaces/components/searchAutocomplete';
import { searchProperty } from '@src/v3/services/properties';
import { useWindowSize } from '@react-hook/window-size';
import { isMobile } from '@src/v3/utils/helper';
import AsyncSelect from 'react-select/async';
import Button from '@src/v3/components/coreUI/button';
const SearchAutocomplete: React.FC<AddressAutoSearchProps> = ({
  onSelect,
  onBlur,
  defaultInputValue,
  defaultValue = [],
  searchType = '',
  extraClasses,
  autoFocus = false,
  placeholder = 'Search for Neighborhoods, State, Zip',
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef: any = React.useRef(null);
  const [width] = useWindowSize();

  // Auto focus the select when autoFocus is true
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (autoFocus && selectRef.current) {
      // Small delay to ensure the component is fully mounted
      timer = setTimeout(() => {
        selectRef.current?.focus();
      }, 50);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [autoFocus]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<
    {
      label: string;
      value: string;
      types: string;
      terms: { offset: number; value: string }[];
    }[]
  >([]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [componentOptions, setComponentOptions] = useState<any>(null);
  const [selectedOptions, setSelectedOptions] =
    useState<AddressAutoSearch[]>(defaultValue);

  const loadOptions = async (
    inputValue: string,
    // eslint-disable-next-line no-unused-vars
    callback: (options: Options<{ label: string; value: string }>) => void
  ): Promise<void> => {
    if (inputValue.length >= 3) {
      setIsLoading(true);

      try {
        let restrict = '';
        if (selectedOptions.length > 0) {
          restrict = selectedOptions[0].types || searchType || '';
        }

        const result = await searchProperty(
          inputValue,
          restrict,
          false,
          defaultValue[0]?.main_text || ''
        );

        if (result && result.length > 0) {
          const options = result.map(
            (item: { data: string; display: string; type: string }) => ({
              label: item.display,
              value: item.data,
              types: item.type,
              terms: '',
            })
          );
          setOptions(options);
          callback(options);
          return;
        }

        setOptions([]);
        callback([]);
      } catch (e) {
        console.error(e);
        callback([]);
      } finally {
        selectRef?.current?.focus();
        setIsLoading(false);
      }
    } else {
      callback([]);
    }
  };

  const debouncedLoadOptions = debounce(
    (
      inputValue: string,
      callback: (
        // eslint-disable-next-line no-unused-vars
        options: Options<{ label: string; value: string }>
      ) => void | Promise<Options<{ label: string; value: string }>>
    ) => {
      loadOptions(inputValue, callback);
    },
    800
  );

  const handleInputChange = (inputValue: string) => {
    debouncedLoadOptions(inputValue, () => {
      return;
    });
  };

  const handleChange = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedOption: any
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectedItems = selectedOption.map((option: any) => {
      return {
        address: option?.label || '',
        place_id: option?.value || '',
        main_text: option?.value || '',
        secondary_text: '',
        types: option?.types || searchType || '',
      };
    });

    onSelect(selectedItems);
    setSelectedOptions(selectedItems);
    setOptions([]);
  };

  const MoreSelectedBadge = ({ items }: { items: any }) => {
    const { length = 0 } = items;
    const label = `${length - 1} +`;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number }>({
      top: 0,
      left: 0,
    });

    // Function to update position
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
    };

    // Update position when opening and on scroll/resize
    useEffect(() => {
      if (!isOpen) {
        return undefined;
      }

      updatePosition();

      const handleScrollOrResize = () => {
        updatePosition();
      };

      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);

      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        const target = event.target as Node;
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(target) &&
          buttonRef.current &&
          !buttonRef.current.contains(target)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [isOpen]);

    return (
      <>
        <Button
          ref={buttonRef}
          size="xs"
          className="bg-blue-100 hover:bg-blue-100 rounded-md text-sm px-3 h-7 order-[0] whitespace-nowrap text-gray-1000 shadow-none relative "
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          title={label}
        >
          {label}
        </Button>
        {isOpen &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed w-auto min-w-[200px] max-w-[200px] p-3 bg-white rounded-md shadow-lg border border-gray-200 z-[9999]"
              style={{ top: position.top, left: position.left }}
            >
              <div className="text-11 font-medium mb-2">Your Selection</div>
              <div className="flex flex-wrap gap-1">
                {(items || [])?.slice(1)?.map((item: any) => item)}
              </div>
            </div>,
            document.body
          )}
      </>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomControl = (props: any) => {
    return <components.Control {...props}></components.Control>;
  };

  const CustomValueContainer = ({ children, ...props }: any) => {
    const [values, input] = children;

    const maxToShow = 1;

    return (
      <components.ValueContainer
        {...props}
        onTouchStart={(event: React.TouchEvent) => {
          event.stopPropagation();
        }}
      >
        {/* display if only one value is selected */}
        {props.hasValue && Number(values?.length) === maxToShow
          ? values[0]
          : null}

        {/* Display single value and +{Items.length} block*/}
        {props.hasValue && Number(values?.length) > maxToShow ? (
          <>
            {values[0]}
            <MoreSelectedBadge items={values} />
          </>
        ) : null}

        {/* Only display placeholder element */}
        {!props.hasValue ? values : null}

        {/* input field */}
        {input}
      </components.ValueContainer>
    );
  };

  useEffect(() => {
    setComponentOptions(
      isMobile() && width && width <= 768
        ? { ValueContainer: CustomValueContainer, Control: CustomControl }
        : {
            ValueContainer: CustomValueContainer,
          }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBlur = () => {
    if (onBlur) {
      onBlur(selectedOptions.length > 0);
    }
  };

  return (
    <AsyncSelect
      isMulti
      autoFocus={autoFocus}
      defaultValue={(defaultValue || []).map((singleValue) => {
        return {
          label: singleValue?.address || '',
          value: singleValue?.main_text || '',
        };
      })}
      ref={selectRef}
      loadOptions={debouncedLoadOptions}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      isLoading={isLoading}
      loadingMessage={() => 'Loading...'}
      options={options}
      isClearable={true}
      defaultInputValue={defaultInputValue}
      className={`zm-select-wrap zm-address-select ${extraClasses}`}
      classNamePrefix="zm-select"
      components={componentOptions}
      isSearchable={
        selectedOptions.length === 1 && searchType === 'exact_address'
          ? false
          : true
      }
      instanceId={`search-autocomplete`}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      // menuPosition="fixed"
      // menuIsOpen={true}
      styles={{
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
        menu: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  );
};

export default SearchAutocomplete;
